// /hoteles/:hotelId — SSR head() with per-hotel SEO. When the hotel exists
// in hotel_static_pages (by hotel_id), we emit rich Hotel schema; otherwise
// we fall back to a generic title + noindex (Iterpec-only live listing).
import { createFileRoute } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/lib/route-boundaries";
import { getHost } from "@/lib/host";
import HotelDetail from "@/pages/HotelDetail";
import { supabase } from "@/integrations/supabase/client";
import {
  canonicalOriginFromHost,
  localeFromHost,
  type LocaleKey,
} from "@/lib/locale-from-host";
import { buildHreflangLinks } from "@/lib/hreflang";

function pickDescription(
  locale: LocaleKey,
  row: {
    description_es: string | null;
    description_en: string | null;
    description_pt: string | null;
  }
): string | null {
  const raw =
    locale === "pt-BR"
      ? row.description_pt
      : locale === "en" || locale === "en-EU"
        ? row.description_en
        : row.description_es;
  if (!raw) return null;
  const plain = raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return plain.length > 300 ? `${plain.slice(0, 297)}…` : plain;
}

export const Route = createFileRoute("/hoteles/$hotelId")({
  loader: async ({ params }) => {
    const host = getHost();
    const locale = localeFromHost(host);
    const origin = canonicalOriginFromHost(host);

    const idNum = Number(params.hotelId);
    let hotel: {
      name: string;
      slug: string | null;
      city: string | null;
      country: string | null;
      address: string | null;
      latitude: number | null;
      longitude: number | null;
      star_rating: number | null;
      description: string | null;
      image: string | null;
    } | null = null;

    if (Number.isFinite(idNum)) {
      const { data } = await supabase
        .from("hotel_static_pages")
        .select(
          "slug,name,city,country,address,latitude,longitude,star_rating,images,description_es,description_en,description_pt"
        )
        .eq("hotel_id", String(idNum))
        .maybeSingle();

      if (data) {
        const images = Array.isArray(data.images)
          ? (data.images.filter((i) => typeof i === "string") as string[])
          : [];
        hotel = {
          slug: data.slug,
          name: data.name,
          city: data.city,
          country: data.country,
          address: data.address,
          latitude: data.latitude != null ? Number(data.latitude) : null,
          longitude: data.longitude != null ? Number(data.longitude) : null,
          star_rating: data.star_rating,
          description: pickDescription(locale, data),
          image: images[0] ?? null,
        };
      } else {
        // Fallback: T4W cached static data (no curated page yet).
        const [{ data: t4w }, { data: desc }] = await Promise.all([
          supabase
            .from("t4w_hotels_static")
            .select("hotel_id,name,category,address,latitude,longitude,destination_id,cover_image_url,cangooroo_thumb_url")
            .eq("hotel_id", idNum)
            .maybeSingle(),
          supabase
            .from("t4w_hotel_descriptions")
            .select("locale,short_description,long_description")
            .eq("hotel_id", idNum),
        ]);

        if (t4w) {
          let city: string | null = null;
          let country: string | null = null;
          if (t4w.destination_id) {
            const { data: dest } = await supabase
              .from("t4w_destinations")
              .select("name,name_en,name_es,name_pt,country_code")
              .eq("id", t4w.destination_id)
              .maybeSingle();
            if (dest) {
              const localizedName =
                locale === "pt-BR" ? dest.name_pt :
                locale === "en" || locale === "en-EU" ? dest.name_en :
                dest.name_es;
              city = localizedName ?? dest.name ?? null;
              country = dest.country_code ?? null;
            }
          }
          const preferredLocale = locale === "pt-BR" ? "pt" : "en";
          const descRow =
            desc?.find((d) => d.locale === preferredLocale) ??
            desc?.find((d) => d.locale === "en") ??
            desc?.[0] ?? null;
          const raw = descRow?.long_description ?? descRow?.short_description ?? null;
          const plain = raw
            ? raw.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
            : null;
          hotel = {
            slug: null,
            name: t4w.name,
            city,
            country,
            address: t4w.address,
            latitude: t4w.latitude != null ? Number(t4w.latitude) : null,
            longitude: t4w.longitude != null ? Number(t4w.longitude) : null,
            star_rating: t4w.category != null ? Math.round(Number(t4w.category)) : null,
            description: plain ? (plain.length > 300 ? `${plain.slice(0, 297)}…` : plain) : null,
            image: t4w.cover_image_url ?? t4w.cangooroo_thumb_url ?? null,
          };
          // Fire-and-forget: if the cover still points at the Cangooroo CDN,
          // copy it to hotel-photos/t4w/<id>/cover.<ext> so the next visit
          // benefits from a same-origin LCP asset. Never awaits or blocks SSR.
          const coverIsRemote =
            !!t4w.cover_image_url && !t4w.cover_image_url.includes("/storage/v1/object/public/hotel-photos/");
          if (coverIsRemote) {
            try {
              void fetch(
                `${(import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/$/, "")}/functions/v1/t4w-hydrate-cover`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
                    Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? ""}`,
                  },
                  body: JSON.stringify({ hotel_id: idNum }),
                },
              ).catch(() => undefined);
            } catch {
              // never block SSR on hydration side-effects
            }
          }
        }

      }
    }

    return { hotel, locale, origin };
  },
  head: ({ params, loaderData }) => {
    const origin = loaderData?.origin ?? "https://www.foradarota.com";
    const url = `${origin}/hoteles/${params.hotelId}`;
    const hotel = loaderData?.hotel;

    if (!hotel) {
      // Live Iterpec-only listing: keep out of the index; canonical to the
      // static slug page if we ever have one.
      return {
        meta: [
          { title: "Hotel | Fora da Rota" },
          { name: "robots", content: "noindex,follow" },
          { property: "og:url", content: url },
        ],
      };
    }

    const locationSuffix = [hotel.city, hotel.country].filter(Boolean).join(", ");
    const title = locationSuffix
      ? `${hotel.name} — ${locationSuffix} | Fora da Rota`
      : `${hotel.name} | Fora da Rota`;
    const description =
      hotel.description ??
      `Reserva ${hotel.name}${locationSuffix ? ` en ${locationSuffix}` : ""} con Fora da Rota.`;
    const image = hotel.image ?? `${origin}/og-image.jpg`;
    // Prefer the static slug page as the canonical URL when available.
    const canonical = hotel.slug ? `${origin}/hotel/${hotel.slug}` : url;

    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Hotel",
      name: hotel.name,
      url: canonical,
      description,
      image,
    };
    if (hotel.star_rating) {
      jsonLd.starRating = { "@type": "Rating", ratingValue: hotel.star_rating };
    }
    if (hotel.address || hotel.city || hotel.country) {
      jsonLd.address = {
        "@type": "PostalAddress",
        streetAddress: hotel.address ?? undefined,
        addressLocality: hotel.city ?? undefined,
        addressCountry: hotel.country ?? undefined,
      };
    }
    if (hotel.latitude != null && hotel.longitude != null) {
      jsonLd.geo = {
        "@type": "GeoCoordinates",
        latitude: hotel.latitude,
        longitude: hotel.longitude,
      };
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...buildHreflangLinks(`/hoteles/${params.hotelId}`),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
      ],
    };
  },
  component: HotelDetail,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: () => <RouteNotFoundBoundary message="No encontramos este hotel." />,
});
